// Icons
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import InventoryIcon from "@mui/icons-material/Inventory";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  GridLegacy as Grid,
  IconButton,
  Modal,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
// Custom components and hooks
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { PageHeader } from "@/components/common/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { paths } from "@/routes/paths";
// Services and types
import { type InventoryItem, InventoryService, type ProductDetailsResponse } from "@/services";
import { CoreService } from "@/services/core/CoreService";
import { usePermission } from "@/utils/permissionManager";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
      style={{ padding: 0 }}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `product-tab-${index}`,
    "aria-controls": `product-tabpanel-${index}`,
  };
}

const ProductDetail: React.FC = () => {
  const { productId: skuId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isCraveUser } = usePermission(user?.role || "");

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Get store ID and product ID from location state
  const storeId = location.state?.storeId;
  const selectedCustomer = location.state?.selectedCustomer;
  const selectedStore = location.state?.selectedStore;

  // Services
  const coreService = useMemo(() => CoreService.getInstance(), []);
  const inventoryService = useMemo(
    () => new InventoryService(coreService.getAuthorizationHeader() || ""),
    [coreService],
  );

  // State
  const [state, setState] = useState<{
    loading: boolean;
    error: string | null;
    data: ProductDetailsResponse | null;
  }>({
    loading: true,
    error: null,
    data: null,
  });

  // Barcode modal state
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [selectedBarcode, setSelectedBarcode] = useState<{ url: string; sku: string } | null>(null);

  const handleOpenBarcodeModal = (gtin: string | undefined, skuId: string) => {
    if (!gtin) return;

    setSelectedBarcode({
      url: `${inventoryService.getBaseUrl()}/barcode/${gtin}`,
      sku: skuId,
    });
    setBarcodeModalOpen(true);
  };

  const handleCloseBarcodeModal = () => {
    setBarcodeModalOpen(false);
  };

  // Ensure only Crave users can access this page
  useEffect(() => {
    if (!isCraveUser()) {
      navigate(paths.storeInsights.fittingRoomsLiveView);
    }
  }, [isCraveUser, navigate]);

  // Ensure we have a store ID
  useEffect(() => {
    if (!storeId) {
      navigate("/admin/experimental/inventory");
    }
  }, [storeId, navigate]);

  // Fetch product details
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!skuId || !storeId) return;

      try {
        setState((prev) => ({ ...prev, loading: true, error: null }));

        const response = await inventoryService.fetchProductDetails(storeId, skuId);
        setState((prev) => ({ ...prev, loading: false, data: response }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : t("inventory.productDetails.errors.failedFetchProductDetails"),
        }));
      }
    };

    fetchProductDetails();
  }, [skuId, storeId, inventoryService]);

  if (state.loading) {
    return (
      <Container maxWidth={false}>
        <PageHeader title={t("inventory.productDetail")} />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (state.error) {
    return (
      <Container maxWidth={false}>
        <PageHeader title={t("inventory.productDetail")} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {state.error}
        </Alert>
      </Container>
    );
  }

  if (!state.data) {
    return (
      <Container maxWidth={false}>
        <PageHeader title={t("inventory.productDetail")} />
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("inventory.productNotFound")}
        </Alert>
      </Container>
    );
  }

  const { data: productData } = state.data;
  const firstSku = productData.skus?.[0];

  // Add check for empty skus array
  if (!productData.skus?.length) {
    return (
      <Container maxWidth={false}>
        <PageHeader title={t("inventory.productDetail")} />
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("inventory.noVariantsFound")}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <PageHeader
        title={t("inventory.productDetail")}
        headerActions={
          <Button
            variant="outlined"
            onClick={() => {
              // Create URL with query parameters using URLSearchParams
              const params = new URLSearchParams();

              // Add customer and store IDs if they exist
              if (selectedCustomer && typeof selectedCustomer.id === "number") {
                params.set("customerId", selectedCustomer.id.toString());
              }

              if (selectedStore && typeof selectedStore.id === "number") {
                params.set("storeId", selectedStore.id.toString());
              }

              // Use React Router's navigate function with the full URL to force a page reload
              // This prevents any client-side state management issues
              const url = `/inventory?${params.toString()}`;

              // Force page reload to avoid state management issues in the Inventory component
              window.location.href = url;
            }}
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("inventory.backToInventory")}
          </Button>
        }
      />

      {/* Product Summary Card - Always visible */}
      <Card sx={{ mb: 3 }}>
        <Grid container spacing={2} sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid
            item
            xs={12}
            sm={3}
            md={2}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
              mb: { xs: 1, sm: 0 },
            }}
          >
            {firstSku?.imageUrl && (
              <img
                src={firstSku.imageUrl}
                alt={firstSku.title || ""}
                style={{
                  width: "120px",
                  height: "120px",
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px solid #eaeaea",
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={9} md={10}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    mb: { xs: 0.5, sm: 1 },
                    pr: { xs: 0, sm: 2 },
                    wordBreak: "break-word",
                  }}
                >
                  {firstSku?.title}
                </Typography>

                {firstSku?.url && (
                  <Tooltip title={t("inventory.tooltips.viewOnRetailerWebsite")}>
                    <IconButton
                      size="small"
                      color="primary"
                      component="a"
                      href={firstSku.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ flexShrink: 0 }}
                    >
                      <LaunchIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Typography variant="caption" color="text.secondary">
                      {t("inventory.brand")}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {firstSku?.brand}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Typography variant="caption" color="text.secondary">
                      {t("inventory.category")}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {firstSku?.category}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Typography variant="caption" color="text.secondary">
                      {t("inventory.productId")}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {firstSku?.productId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Typography variant="caption" color="text.secondary">
                      {t("inventory.totalVariants")}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {productData.skus.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8} md={6} lg={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t("inventory.productDetails.fields.promotion")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: firstSku?.promotion ? "success.main" : "text.secondary",
                        fontWeight: firstSku?.promotion ? "medium" : "normal",
                        fontStyle: firstSku?.promotion ? "normal" : "italic",
                      }}
                    >
                      {firstSku?.promotion || t("inventory.productDetails.fields.noPromotion")}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label={t("inventory.productDetails.aria.tabs")}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                minWidth: { xs: "auto", sm: 120 },
                px: { xs: 1, sm: 2 },
              },
            }}
          >
            <Tab
              icon={<InfoIcon fontSize="small" />}
              iconPosition="start"
              label={t("inventory.productDetails.sections.details")}
              {...a11yProps(0)}
            />
            <Tab
              icon={<InventoryIcon fontSize="small" />}
              iconPosition="start"
              label={t("inventory.variants")}
              {...a11yProps(1)}
            />
          </Tabs>
        </Box>
      </Card>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              {t("inventory.productDetails.sections.details")}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {firstSku?.description && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  {t("inventory.productDetails.fields.description")}
                </Typography>
                <Typography variant="body2" paragraph sx={{ whiteSpace: "pre-line" }}>
                  {firstSku.description}
                </Typography>
              </Box>
            )}

            {/* Additional product details could go here */}
          </Box>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" gutterBottom>
              {t("inventory.variants")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("inventory.variantsDescription")}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              p: { xs: 1.5, sm: 2 },
              backgroundColor: "rgba(0, 0, 0, 0.02)",
              gap: { xs: 1, sm: 0 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {t("inventory.allVariantsWithCount", {
                  count: productData.skus.length,
                })}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label={`${productData.skus.filter((sku) => sku.quantity > 0).length} ${t("inventory.inStock")}`}
                size="small"
                color="success"
                variant="outlined"
              />
              <Chip
                label={`${productData.skus.filter((sku) => sku.quantity === 0).length} ${t("inventory.outOfStock")}`}
                size="small"
                color="error"
                variant="outlined"
              />
            </Box>
          </Box>

          {/* Desktop View: Table */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              overflow: "auto",
            }}
          >
            <TableContainer sx={{ minWidth: "100%" }}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("inventory.sku")}</TableCell>
                    <TableCell>{t("inventory.productDetails.fields.image")}</TableCell>
                    <TableCell>{t("inventory.color")}</TableCell>
                    <TableCell>{t("inventory.size")}</TableCell>
                    {productData.size2 && (
                      <TableCell>{firstSku?.size2Label || t("inventory.size2")}</TableCell>
                    )}
                    <TableCell align="right">{t("inventory.quantity")}</TableCell>
                    <TableCell align="right">{t("inventory.price")}</TableCell>
                    <TableCell align="right">
                      {t("inventory.productDetails.fields.promotion")}
                    </TableCell>
                    <TableCell align="right">{t("inventory.status")}</TableCell>
                    <TableCell align="center">{t("inventory.barcode")}</TableCell>
                    <TableCell align="center">{t("inventory.productDetails.fields.url")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productData.skus
                    .slice() // Create a copy to avoid mutating the original array
                    .sort((a, b) => {
                      // Sort by stock status (in-stock first)
                      if (a.quantity > 0 && b.quantity === 0) return -1;
                      if (a.quantity === 0 && b.quantity > 0) return 1;
                      // Secondary sort by color
                      return (a.color || "").localeCompare(b.color || "");
                    })
                    .map((sku: InventoryItem) => (
                      <TableRow
                        key={sku.skuId}
                        sx={{
                          backgroundColor:
                            sku.quantity === 0 ? "rgba(244, 67, 54, 0.05)" : "inherit",
                        }}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{sku.skuId}</TableCell>
                        <TableCell>
                          {sku.imageUrl && (
                            <Box
                              component="img"
                              src={sku.imageUrl}
                              alt={sku.title || ""}
                              sx={{
                                width: "50px",
                                height: "50px",
                                objectFit: "cover",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                bgcolor: sku.colorCode || sku.color,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                            {sku.color}
                          </Box>
                        </TableCell>
                        <TableCell>{sku.size}</TableCell>
                        {productData.size2 && <TableCell>{sku.size2}</TableCell>}
                        <TableCell align="right">{sku.quantity}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Box>
                            {sku.price}
                            {sku.salePrice && (
                              <Typography
                                component="span"
                                variant="body2"
                                color="error"
                                sx={{ ml: 1, textDecoration: "line-through" }}
                              >
                                {sku.salePrice}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              color: sku.promotion ? "success.main" : "text.secondary",
                              fontStyle: sku.promotion ? "normal" : "italic",
                              maxWidth: "150px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sku.promotion || t("inventory.productDetails.fields.noPromotion")}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={
                              sku.quantity > 0 ? t("inventory.inStock") : t("inventory.outOfStock")
                            }
                            color={sku.quantity > 0 ? "success" : "error"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {sku.gtin && (
                            <Tooltip title={t("inventory.tooltips.clickToEnlargeBarcode")}>
                              <Box
                                component="img"
                                src={`${inventoryService.getBaseUrl()}/barcode/${sku.gtin}`}
                                alt={t("inventory.barcodeForSku", { sku: sku.skuId })}
                                sx={{
                                  maxWidth: "150px",
                                  height: "auto",
                                  display: "block",
                                  margin: "0 auto",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                  },
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  p: 1,
                                }}
                                onClick={() =>
                                  sku.gtin && handleOpenBarcodeModal(sku.gtin, sku.skuId)
                                }
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {sku.url && (
                            <Tooltip title={t("inventory.tooltips.openProductPage")}>
                              <IconButton
                                size="small"
                                color="primary"
                                component="a"
                                href={sku.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile View: Cards */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {productData.skus
              .slice() // Create a copy to avoid mutating the original array
              .sort((a, b) => {
                // Sort by stock status (in-stock first)
                if (a.quantity > 0 && b.quantity === 0) return -1;
                if (a.quantity === 0 && b.quantity > 0) return 1;
                // Secondary sort by color
                return (a.color || "").localeCompare(b.color || "");
              })
              .map((sku: InventoryItem) => (
                <Paper
                  key={sku.skuId}
                  elevation={0}
                  sx={{
                    m: 1.5,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    backgroundColor: sku.quantity === 0 ? "rgba(244, 67, 54, 0.05)" : "inherit",
                  }}
                >
                  <Grid container spacing={2}>
                    {/* Image and Basic Info */}
                    <Grid item xs={3} sm={3}>
                      {sku.imageUrl ? (
                        <Box
                          component="img"
                          src={sku.imageUrl}
                          alt={sku.title || ""}
                          sx={{
                            width: "100%",
                            aspectRatio: "1/1",
                            objectFit: "cover",
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            maxWidth: "70px",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            aspectRatio: "1/1",
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            maxWidth: "70px",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {t("common.noImage")}
                          </Typography>
                        </Box>
                      )}
                    </Grid>

                    {/* Details */}
                    <Grid item xs={9} sm={9}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {sku.skuId}
                        <Chip
                          label={
                            sku.quantity > 0 ? t("inventory.inStock") : t("inventory.outOfStock")
                          }
                          color={sku.quantity > 0 ? "success" : "error"}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1, height: 20, "& .MuiChip-label": { px: 1, py: 0.25 } }}
                        />
                      </Typography>

                      <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={6}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: sku.colorCode || sku.color,
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            />
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              {sku.color}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {t("inventory.variant.sizeLabel", {
                              size: sku.size,
                              secondarySize:
                                productData.size2 && sku.size2 ? ` / ${sku.size2}` : "",
                            })}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {sku.price}
                            {sku.salePrice && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="error"
                                sx={{ ml: 1, textDecoration: "line-through" }}
                              >
                                {sku.salePrice}
                              </Typography>
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {t("inventory.variant.quantityLabel", { quantity: sku.quantity })}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid
                      item
                      xs={12}
                      sx={{
                        mt: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {sku.gtin ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => sku.gtin && handleOpenBarcodeModal(sku.gtin, sku.skuId)}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {t("inventory.barcode")}
                        </Button>
                      ) : (
                        <Box></Box>
                      )}

                      {sku.url && (
                        <Tooltip title={t("inventory.tooltips.openProductPage")}>
                          <IconButton
                            size="small"
                            color="primary"
                            component="a"
                            href={sku.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <LaunchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              ))}
          </Box>
        </Card>
      </TabPanel>

      {/* Barcode Modal */}
      <Modal
        open={barcodeModalOpen}
        onClose={handleCloseBarcodeModal}
        aria-labelledby="barcode-modal-title"
        aria-describedby="barcode-modal-description"
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              backdropFilter: "blur(3px)",
            },
          },
        }}
        sx={{
          transition: "all 0.3s ease-in-out",
          opacity: barcodeModalOpen ? 1 : 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "auto",
            maxWidth: "95%",
            maxHeight: "95vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            overflow: "auto",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
            }}
          >
            <Typography
              id="barcode-modal-title"
              variant="h6"
              component="h2"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
            >
              {selectedBarcode
                ? t("inventory.barcodeForSku", { sku: selectedBarcode.sku })
                : t("inventory.barcode")}
            </Typography>
            <IconButton onClick={handleCloseBarcodeModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedBarcode && (
            <Box
              component="img"
              src={selectedBarcode.url}
              alt={t("inventory.barcodeForSku", { sku: selectedBarcode.sku })}
              sx={{
                maxWidth: "100%",
                maxHeight: { xs: "calc(80vh - 120px)", sm: "calc(90vh - 120px)" },
                height: "auto",
                minWidth: { xs: 200, sm: 300 },
                border: "1px solid",
                borderColor: "divider",
                p: { xs: 1.5, sm: 3 },
                bgcolor: "white",
              }}
            />
          )}

          <Typography
            id="barcode-modal-description"
            variant="body2"
            color="text.secondary"
            align="center"
          >
            {t("inventory.clickToDismiss")}
          </Typography>

          <Typography
            variant="subtitle2"
            color="primary"
            align="center"
            sx={{ fontWeight: "bold" }}
          >
            {t("inventory.text.scanBarcodeInstructions")}
          </Typography>
        </Box>
      </Modal>
    </Container>
  );
};

export default ProductDetail;
